import dayjs from 'dayjs';
import { sortBy } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { getDefaultWorkQueueState } from './defaultWorkQueueState';
import { useUserPreferences } from 'App/contexts/UserPreferencesContext';
import useFetchTherapeuticCategory from './hooks/useFetchTherapeuticCategory';
import useFetchBranchesQuery from 'App/hooks/useFetchBranchesQuery';
import useFetchPatientLabelsMenu from 'App/hooks/useFetchPatientLabelsMenu';
import { dateOnlySlashesFormat, toDate } from 'App/services/DateService';
import { transformLsFilterToReact } from './workQueue.utils';
import { FILTER_TYPE } from './workQueue.constant';

const propTypes = {
    children: PropTypes.oneOfType([PropTypes.element, PropTypes.node]).isRequired,
    filterType: PropTypes.oneOf([FILTER_TYPE.ASSIGNMENT_MANAGER, FILTER_TYPE.WORK_QUEUE]),
};
const defaultProps = {
    filterType: FILTER_TYPE.WORK_QUEUE,
};
const defaultWorkQueueState = getDefaultWorkQueueState();

const activityTypeOptions = [
    {
        id: 'PCC',
        option: 'Patient Care (PCC)',
    },
    {
        id: 'CLI',
        option: 'Clinical',
    },
    {
        id: 'REF',
        option: 'Referral',
    },
];

const customActivityOptions = [
    { id: 'onlyMine', option: 'Updated By Me' },
    { id: 'assignedToMe', option: 'Assigned To Me' },
    {
        id: 'highRisk',
        option: 'High-Risk Patient',
    },
];

const validationSchema = Yup.object().shape({
    filterParams: Yup.object().shape({
        dateStart: Yup.date()
            .nullable()
            .test('dateStart-before-dateEnd', function test(value) {
                const { dateEnd, type } = this.parent;
                if (type !== 'custom') {
                    return true;
                }
                if (!value) {
                    return this.createError({
                        path: 'filterParams.dateStart',
                        message: 'The date range start is required',
                    });
                }
                if (dateEnd && dayjs(value).isAfter(dayjs(dateEnd))) {
                    return this.createError({
                        path: 'filterParams.dateStart',
                        message: `The date range start cannot be past ${dayjs(dateEnd).format(dateOnlySlashesFormat)}`,
                    });
                }
                return true;
            }),
        dateEnd: Yup.date()
            .nullable()
            .test('dateEnd-after-dateStart', function test(value) {
                const { dateStart, type } = this.parent;
                if (type !== 'custom') {
                    return true;
                }
                if (!value) {
                    return this.createError({
                        path: 'filterParams.dateEnd',
                        message: 'The date range end is required',
                    });
                }
                if (!dateStart || dayjs(value).isBefore(dayjs(dateStart))) {
                    return this.createError({
                        path: 'filterParams.dateEnd',
                        message: `The date range end cannot be before ${dayjs(dateStart).format(dateOnlySlashesFormat)}`,
                    });
                }
                return true;
            }),
    }),
});

function QueueProvider({ children, filterType }) {
    const { getPreference } = useUserPreferences();
    const { data: availableTherapies, isLoading: isTherapiesLoading } = useFetchTherapeuticCategory();
    const { data: branchList, isLoading: isBranchesLoading } = useFetchBranchesQuery();
    const { data: availablePatientLabels, isLoading: isPatientLabelsLoading } = useFetchPatientLabelsMenu();

    const therapyOptions = useMemo(() => {
        const therapies = Object.values(availableTherapies || {}).map((opt) => ({ id: opt.id, option: opt.name }));
        return sortBy(therapies, 'option');
    }, [availableTherapies]);

    const branchOptions = useMemo(() => {
        const branches = Object.values(branchList || {}).map((opt) => ({ id: opt.id, option: opt.name }));
        return sortBy(branches, 'option');
    }, [branchList]);

    const patientLabelOptions = useMemo(() => {
        const labels = (availablePatientLabels || []).map((opt) => ({ id: opt.valueName, option: opt.valueName }));
        return sortBy(labels, 'option');
    }, [availablePatientLabels]);

    const getInitialValues = useCallback(() => {
        let storedFilter;
        storedFilter = getPreference(`${filterType}_selectedFilter`, true);
        storedFilter = transformLsFilterToReact(storedFilter);
        if (storedFilter) {
            storedFilter = {
                ...storedFilter,
                dateEnd: toDate(storedFilter.dateEnd),
                dateStart: toDate(storedFilter.dateStart),
            };
        }
        const filter = storedFilter || defaultWorkQueueState.filterParams;
        const defaultActiveSavedFilter = getPreference(`${filterType}_activeSavedFilter`);

        return {
            ...defaultWorkQueueState,
            filterParams: {
                ...defaultWorkQueueState.filterParams,
                ...filter,
            },
            mobileAnswersFilterParams:
                storedFilter?.mobileAnswersFilterParams || defaultWorkQueueState.mobileAnswersFilterParams,
            activeSavedFilter: parseInt(defaultActiveSavedFilter, 10) ?? null,
            allCategoriesList: therapyOptions,
            allActivityTypesList: activityTypeOptions,
            allCustomOptionsList: customActivityOptions,
            allBranchesList: branchOptions,
            allPatientLabelsList: patientLabelOptions,
            filterType,
        };
    }, [filterType, therapyOptions, branchOptions, patientLabelOptions, getPreference]);

    return (
        !isTherapiesLoading &&
        !isBranchesLoading &&
        !isPatientLabelsLoading && (
            <Formik initialValues={getInitialValues()} validationSchema={validationSchema} onSubmit={() => {}}>
                {({ handleSubmit, resetForm }) => {
                    return (
                        <Form onSubmit={handleSubmit} onReset={resetForm} className="work-queue-form">
                            {children}
                        </Form>
                    );
                }}
            </Formik>
        )
    );
}
QueueProvider.propTypes = propTypes;
QueueProvider.defaultProps = defaultProps;

export { QueueProvider };
